export const SCENARIO_CHAIN_COUPLING_FAMILIES = [
  'market_to_purchased_transport',
  'market_to_gross_revenue',
  'sell_side_pass_through',
  'net_revenue_identity',
  'ratio_recompute',
  'opex_response',
  'intervention_exchange',
] as const;

export type ScenarioChainCouplingFamily =
  (typeof SCENARIO_CHAIN_COUPLING_FAMILIES)[number];

export type ScenarioChainGrain = 'fiscal_year';

export type ScenarioChainPolicy = Readonly<{
  maxConditionNodes: number;
  maxInterventionNodes: number;
  maxAssumedEdges: number;
  boundsUsabilityRatio: number;
}>;

export const DEFAULT_SCENARIO_CHAIN_POLICY: ScenarioChainPolicy = Object.freeze({
  maxConditionNodes: 2,
  maxInterventionNodes: 1,
  maxAssumedEdges: 0,
  boundsUsabilityRatio: 2,
});

export type ScenarioChainGapCode =
  | 'chain_coupling_missing'
  | 'chain_regime_crossed'
  | 'chain_depth_exceeded'
  | 'chain_bounds_unusable';

export type ScenarioChainStatus = 'chained' | 'partially_chained' | 'blocked';

export type ScenarioChainAdmissionKeyInput = Readonly<{
  family: ScenarioChainCouplingFamily;
  sourceSeriesId: string;
  targetMetricKey: string;
  grain: ScenarioChainGrain;
}>;

export type ScenarioChainDemandExposureBasis =
  'brokerage_volume_sensitivity';

export type ScenarioChainDemandAssumptionClass =
  | 'observed_tenant_sensitivity'
  | 'officer_attested_sensitivity'
  | 'extrapolated_elasticity_assumption';

type ScenarioChainAdmissionRowBase = Omit<
  ScenarioChainAdmissionKeyInput,
  'family'
> &
  Readonly<{
    tenantId: string;
    admissionId: string;
    formulaId: string;
    formulaVersion: string;
    admittedSensitivity: number;
    evidenceRefIds: readonly string[];
    sourceClass: string;
    asOf: string;
  }>;

export type ScenarioChainDemandAdmissionRow = ScenarioChainAdmissionRowBase &
  Readonly<{
    family: 'market_to_gross_revenue';
    exposureBasis: ScenarioChainDemandExposureBasis;
    assumptionClass: ScenarioChainDemandAssumptionClass;
  }>;

export type ScenarioChainAdmissionRow =
  | ScenarioChainDemandAdmissionRow
  | (ScenarioChainAdmissionRowBase &
      Readonly<{
        family: Exclude<
          ScenarioChainCouplingFamily,
          'market_to_gross_revenue'
        >;
        exposureBasis?: never;
        assumptionClass?: never;
      }>);

export type ScenarioChainGap = Readonly<{
  code: ScenarioChainGapCode;
  label: string;
  missingKeys: readonly string[];
}>;

export type ScenarioChainSourceClass =
  | 'approved_source'
  | 'industry_reference'
  | 'scenario'
  | 'modeled'
  | 'gated';

const SCENARIO_CHAIN_SOURCE_CLASS_RANK: Readonly<Record<ScenarioChainSourceClass, number>> =
  Object.freeze({
    approved_source: 0,
    industry_reference: 1,
    scenario: 2,
    modeled: 3,
    gated: 4,
  });

export function buildScenarioChainAdmissionKey(
  input: ScenarioChainAdmissionKeyInput,
): string {
  return [
    input.family,
    input.sourceSeriesId,
    input.targetMetricKey,
    input.grain,
  ].join('|');
}

export function resolveScenarioChainAdmission(
  rows: readonly ScenarioChainAdmissionRow[],
  input: ScenarioChainAdmissionKeyInput & Readonly<{ tenantId: string }>,
): ScenarioChainAdmissionRow | null {
  const key = buildScenarioChainAdmissionKey(input);
  return (
    rows.find(
      (row) =>
        row.tenantId === input.tenantId &&
        buildScenarioChainAdmissionKey(row) === key,
    ) ?? null
  );
}

export function scenarioChainCouplingMissingGap(
  input: ScenarioChainAdmissionKeyInput,
): ScenarioChainGap {
  const missingKey = buildScenarioChainAdmissionKey(input);
  return {
    code: 'chain_coupling_missing',
    label: 'Scenario chain coupling missing',
    missingKeys: [missingKey],
  };
}

export function mergeScenarioChainSourceClass(
  values: readonly (string | null | undefined)[],
): ScenarioChainSourceClass {
  let selected: ScenarioChainSourceClass = 'approved_source';
  for (const value of values) {
    const normalized = normalizeScenarioChainSourceClass(value);
    if (
      SCENARIO_CHAIN_SOURCE_CLASS_RANK[normalized] >
      SCENARIO_CHAIN_SOURCE_CLASS_RANK[selected]
    ) {
      selected = normalized;
    }
  }
  return selected;
}

function normalizeScenarioChainSourceClass(
  value: string | null | undefined,
): ScenarioChainSourceClass {
  const normalized = (value ?? '').trim().toLowerCase().replace(/-/g, '_');
  if (normalized === 'modeled') return 'modeled';
  if (normalized === 'scenario') return 'scenario';
  if (normalized === 'industry_reference') return 'industry_reference';
  if (normalized === 'gated' || /(^|_)gate(d)?(_|$)/.test(normalized)) {
    return 'gated';
  }
  return 'approved_source';
}
